import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton, MatIconButton } from '@angular/material/button';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { TasksService } from 'app/modules/admin/tasks/tasks.service';
import { environment } from 'environments/environment';
import { webpages_Role } from 'app/shared/models/domain/data.model';
import { AuthService } from 'app/core/auth/auth.service';
import { PageRequest } from 'app/shared/models/request/page-request.model';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { NgIf, NgFor, NgClass, NgTemplateOutlet, DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTabGroup, MatTab, MatTabLabel, MatTabContent } from '@angular/material/tabs';
import { MatRipple } from '@angular/material/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { RouterLink } from '@angular/router';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    imports: [MatIconButton, NgIf, MatIcon, MatTooltip, MatTabGroup, MatTab, MatTabLabel, MatTabContent, MatButton, MatRipple, MatCheckbox, NgFor, NgClass, NgTemplateOutlet, RouterLink, DatePipe]
})
export class NotificationsComponent implements OnChanges, OnInit, OnDestroy {
    @Input() allItems: Notification[];
    @Input() notifications: Notification[];
    @Input() alerts: Notification[];
    @Input() chats: Notification[];
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;
    filteredNotifications: Notification[];
    filteredAlerts: Notification[];
    filteredChats: Notification[];
    unreadNotifications: boolean = false;
    unreadChats: boolean = false;
    isClient: boolean = false;
    isCustomer: boolean = false;
    isAdmin: boolean = false;
    isAuditor: boolean = false;
    unreadAlerts: boolean = false;
    unreadCount: number = 0;
    unreadAlertsCount: number = 0;
    unreadNotificationsCount: number = 0;
    auditsPerPage: number = 1000;
    audits: any;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _tasksService: TasksService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _authService: AuthService,
        private _dashService: DashboardService
    ) {
        //repeat every 10 minutes
        // timer(0, 10 * 60 * 1000).subscribe(() => {
            timer(0, 10 * 60 * 1000).subscribe(() => {
                this.loadNotifications();
            });

        const roles = this._authService.localUserRoles;
        roles.forEach((role: webpages_Role) => {
            if (environment.roleTabs['customer'].indexOf(role.roleName) > -1) {
                this.isCustomer = true;
            }

            if (environment.roleTabs['client'].indexOf(role.roleName) > -1) {
                this.isClient = true;
            }

            if (environment.roleTabs['admin'].indexOf(role.roleName) > -1) {
                this.isAdmin = true;
            }

            if (environment.roleTabs['auditor'].indexOf(role.roleName) > -1) {
                this.isAuditor = true;
            }
        });
        if (this.isClient) {
            const auditsRequest = new PageRequest();
            auditsRequest.itemsPerPage = this.auditsPerPage;
            auditsRequest.sort = 'auditNumber';
            auditsRequest.sortDir = 'desc';
            auditsRequest.clientId = '';
            auditsRequest.deleted = false;
            this._dashService.getCustomerAudits(auditsRequest)
                .subscribe((audits) => {
                    this.audits = audits;
                    const filterAuditsId: number[] = this.audits?.items.map((rows: any) => rows.auditNumber) || [];
                    this._tasksService.getTasksPublicForNotificationForDashboard(filterAuditsId,'desc',false).subscribe((res: any) => {
                        const items = this.processItems(res?.items);
                        this.allItems = [...(this.allItems || []), ...items];
                        this.chats = res?.items;
                        this.filteredChats = this.chats;
                        this._calculateUnreadCount();
                    });
                });
        }

    }

    loadNotifications(): void {
        this._tasksService.getTasksPublicForNotification(this.isClient ? true : false, this._authService?.localClientId).subscribe((res: any) => {
            const items = this.processItems(res?.items);
            this.allItems = items;
            this.notifications = items?.filter(rows => rows.type != 'alert');
            this.alerts = items?.filter(rows => rows.completedAt == null && rows.type == 'alert');

            this.filteredNotifications = this.notifications;
            this.filteredAlerts = this.alerts;

            this._calculateUnreadCount();
            this._changeDetectorRef.markForCheck();
        });
    }

    processItems(items): any {
        const roles = this._authService.localUserRoles;
        let isCustomer = false;
        let isClient = false;
        let isAdmin = false;
        let isAuditor = false;
        roles.forEach((role: webpages_Role) => {
            if (environment.roleTabs['customer'].indexOf(role.roleName) > -1) {
                isCustomer = true;
            }

            if (environment.roleTabs['client'].indexOf(role.roleName) > -1) {
                isClient = true;
            }

            if (environment.roleTabs['admin'].indexOf(role.roleName) > -1) {
                isAdmin = true;
            }

            if (environment.roleTabs['auditor'].indexOf(role.roleName) > -1) {
                isAuditor = true;
            }
        });
        return items?.map((rows) => {
            if (rows?.subjectId && rows?.subjectType == 'Audit') {
                rows.useRouter = true;
                if (isAdmin) {
                    rows.link = '/admin/audits/details/' + rows?.subjectId + '/audit-details';
                }
                else if (isCustomer) {
                    rows.link = '/audit/' + rows?.subjectId + '/audit-details';
                }
                else if (isClient) {
                    rows.link = '/client/audits/details/' + rows?.subjectId;
                }
                else if (isAuditor) {
                    rows.link = '/audit/' + rows?.subjectId + '/audit-details';
                }
            }
            return rows;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('notifications' in changes) {
            this._notificationsService.store(changes.notifications.currentValue);
        }
    }

    ngOnInit(): void {
        this._tasksService.tasksNotification$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: any[]) => {
                const items = this.processItems(notifications);
                this.allItems = items;
                this.notifications = items?.filter(rows => rows.type != 'alert');
                this.alerts = items?.filter(rows => rows.completedAt == null && rows.type == 'alert');

                this.filteredNotifications = this.notifications;
                this.filteredAlerts = this.alerts;
                if (this.isClient) {
                    const auditsRequest = new PageRequest();
                    auditsRequest.itemsPerPage = this.auditsPerPage;
                    auditsRequest.sort = 'auditNumber';
                    auditsRequest.sortDir = 'desc';
                    auditsRequest.clientId = '';
                    auditsRequest.deleted = false;
                    this._dashService.getCustomerAudits(auditsRequest)
                        .subscribe((audits) => {
                            this.audits = audits;
                            const filterAuditsId: number[] = this.audits?.items.map((rows: any) => rows.auditNumber) || [];
                            this._tasksService.getTasksPublicForNotificationForDashboard(filterAuditsId,'desc',false).subscribe((res: any) => {
                                const itemsChat = this.processItems(res?.items);
                                this.allItems = [...(this.allItems || []), ...itemsChat];
                                this.chats = res?.items;
                                this.filteredChats = this.chats;
                                this._calculateUnreadCount();
                            });
                        });
                }
                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }
        if (!this._overlayRef) {
            this._createOverlay();
        }
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    markAllAsRead(res): void {
        const finalArray = [];
        res?.map((rows: any) => {
            if (rows.readAt == null) {
                finalArray.push({ id: rows.id, readAt: new Date() });
            }
        });
        this._tasksService.markAllAsRead(finalArray).subscribe(() => {
            this.loadNotifications();
        });
    }

    toggleRead(notification): void {
        if (notification.readAt == null) {
            notification.readAt = new Date();
        } else {
            notification.readAt = null;
        }
        this._tasksService.updateTaskNotification(notification.id, notification, this.isClient ? 'client' : 'admin').subscribe((res) => {
            this.loadNotifications();
        });
    }

    onlyUnread(type, event): void {
        if (type == 'notifications') {
            this.unreadNotifications = !this.unreadNotifications;
            this.filteredNotifications = this.notifications?.filter(rows => rows.type != 'alert' && rows.type != 'message');
            if (this.unreadNotifications) {
                this.filteredNotifications = this.notifications.filter(rows => rows.readAt == null && rows.type != 'alert' && rows.type != 'message');
            }
        }
        else if (type == 'message') {
            this.unreadChats = !this.unreadChats;
            this.filteredChats = this.chats?.filter(rows => rows.type == 'message');
            if (this.unreadChats) {
                this.filteredChats = this.chats.filter(rows => rows.readAt == null && rows.type == 'message');
            }
        } else {
            this.unreadAlerts = !this.unreadAlerts;
            this.filteredAlerts = this.alerts?.filter(rows => rows.type == 'alert');
            if (this.unreadAlerts) {
                this.filteredAlerts = this.alerts.filter(rows => rows.readAt == null && rows.type == 'alert');
            }
        }

        this._calculateUnreadCount();
        this._changeDetectorRef.markForCheck();
    }

    delete(notification: Notification): void {
        this._notificationsService.delete(notification.id).subscribe();
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition()
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    private _calculateUnreadCount(): void {
        let count = 0;
        if (this.allItems && this.allItems.length) {
            count = this.allItems.filter(notification => notification.readAt == null).length;
        }
        if (this.notifications && this.notifications.length) {
            this.unreadNotificationsCount = this.notifications.filter(notification => notification.readAt == null).length;
        }
        if (this.alerts && this.alerts.length) {
            this.unreadAlertsCount = this.alerts.filter(notification => notification.readAt == null).length;
        }
        this.unreadCount = count;
    }
}
